/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const DELIVERABLES = [
  "Workflow review",
  "AI opportunity map",
  "Top three recommended use cases",
  "Prioritization scorecard",
  "Recommended tools",
  "Risk considerations",
  "Estimated impact",
  "30-day action plan",
];

export default function BuyAssessment() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canceled = params.get("canceled") === "1";

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!email) { setError("Please enter your email."); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/operations/checkout", {
        email,
        business_name: businessName,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setError("Could not start checkout. Please try again.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Checkout failed.";
      setError(typeof msg === "string" ? msg : "Checkout failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="buy-assessment-page" className="min-h-screen bg-[#080807] text-[#f4efe6]">
      <Helmet>
        <title>AI Business Opportunity Assessment — $300 — The Entity Agency</title>
        <meta name="description" content="A structured review of your workflows, tools, and readiness for practical AI adoption. Deliverables: opportunity map, top three use cases, and a 30-day action plan." />
        <link rel="canonical" href="https://theentityagency.com/buy-assessment" />
      </Helmet>

      <section className="border-b border-[#2a2620]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 pt-16 sm:pt-20 pb-8">
          <nav className="font-mono-eav text-[11px] tracking-[0.2em] uppercase text-[#8a7e6d]">
            <Link to="/ai-operations" className="hover:text-[#b8963e]">AI Operations</Link>
            <span className="mx-2">/</span>
            <span className="text-[#b8963e]">Assessment</span>
          </nav>
          <p className="mt-6 overline">$300 · One-time</p>
          <h1 className="mt-3 font-serif-display text-[36px] sm:text-[52px] leading-[1.05] text-[#f4efe6]"
              style={{ fontWeight: 600 }}>
            AI Business Opportunity Assessment.
          </h1>
          <p className="mt-6 text-lg text-[#c9bca4] leading-relaxed max-w-2xl">
            A structured review of your business workflows, operational challenges, existing tools, and highest-value opportunities for practical AI adoption. Includes a prioritized opportunity map, recommended first workflow, risk considerations, and a 30-day implementation plan.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-12 grid md:grid-cols-5 gap-10">
          <div className="md:col-span-3">
            <h2 className="font-serif-display text-[24px] text-[#f4efe6]" style={{ fontWeight: 600 }}>
              What you'll receive
            </h2>
            <ul className="mt-6 space-y-2.5 text-[#c9bca4]">
              {DELIVERABLES.map((d) => (
                <li key={d} className="flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#b8963e] shrink-0 mt-0.5" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 border border-[#2a2620] bg-[#0e0c0a] p-5 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#b8963e] shrink-0 mt-0.5" />
              <p className="text-sm text-[#c9bca4] leading-relaxed">
                Payment is processed securely by Stripe on behalf of OverKnight Enterprises. You'll receive a receipt by email and our team will reach out within one business day to kick off your assessment.
              </p>
            </div>
          </div>

          <div className="md:col-span-2">
            <form onSubmit={startCheckout} data-testid="assessment-checkout-form"
                  className="border border-[#b8963e] bg-[#0e0c0a] p-6 sm:p-8 sticky top-24">
              <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">
                Purchase assessment
              </p>
              <p className="mt-3 font-serif-display text-4xl text-[#f4efe6]" style={{ fontWeight: 700 }}>
                $300<span className="text-base text-[#8a7e6d] font-normal"> USD</span>
              </p>
              {canceled && (
                <p className="mt-3 text-sm text-[#d4a04a]" data-testid="checkout-canceled-msg">
                  Checkout was canceled. Your card was not charged. You can retry anytime.
                </p>
              )}
              <div className="mt-6 space-y-4">
                <label className="block">
                  <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-2">
                    Work email*
                  </span>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                         data-testid="assessment-email-input"
                         className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-4 py-3" />
                </label>
                <label className="block">
                  <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-2">
                    Business name (optional)
                  </span>
                  <input type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                         data-testid="assessment-business-input"
                         className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-4 py-3" />
                </label>
              </div>
              {error && <p className="mt-3 text-sm text-[#c46a5a]" data-testid="checkout-error">{error}</p>}
              <button type="submit" disabled={loading}
                      data-testid="assessment-checkout-btn"
                      className={loading ? "mt-6 btn-gold opacity-60 cursor-not-allowed w-full justify-center" : "mt-6 btn-gold w-full justify-center"}>
                {loading ? "Opening Stripe…" : "Continue to Stripe"} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-4 text-xs text-[#8a7e6d] leading-relaxed">
                Provided by The Entity Agency, an OverKnight Enterprises company.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

// Post-payment thank-you page (public, no auth). Polls the assessment status endpoint.
export function AssessmentThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", polling: true });

  useEffect(() => {
    if (!sessionId) { setStatus({ payment_status: "unknown", polling: false }); return; }
    let alive = true;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/operations/checkout/status/${sessionId}`);
        if (!alive) return;
        setStatus({ ...data, polling: data.payment_status !== "paid" && attempts < 12 });
        if (data.payment_status !== "paid" && attempts < 12) {
          setTimeout(poll, 2500);
        }
      } catch {
        if (alive) setStatus((s) => ({ ...s, polling: false, error: "Could not confirm payment." }));
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId]);

  const paid = status.payment_status === "paid";

  return (
    <div data-testid="assessment-thankyou-page" className="min-h-screen bg-[#080807] text-[#f4efe6]">
      <Helmet>
        <title>Thanks for your purchase — The Entity Agency</title>
      </Helmet>
      <section>
        <div className="max-w-2xl mx-auto px-6 sm:px-8 pt-24 pb-16 text-center">
          {paid ? (
            <>
              <CheckCircle2 className="w-16 h-16 text-[#b8963e] mx-auto" />
              <h1 className="mt-6 font-serif-display text-[36px] sm:text-[48px] leading-[1.05] text-[#f4efe6]"
                  style={{ fontWeight: 600 }}>
                Payment received.
              </h1>
              <p className="mt-6 text-[#c9bca4] leading-relaxed max-w-lg mx-auto">
                Thanks — your AI Business Opportunity Assessment is booked. A team member will reach out within one business day to schedule your kickoff session and gather the details needed for your report.
              </p>
            </>
          ) : status.polling ? (
            <>
              <p className="overline">Confirming payment…</p>
              <h1 className="mt-3 font-serif-display text-[32px] sm:text-[44px] text-[#f4efe6]"
                  style={{ fontWeight: 600 }}>
                Just a moment.
              </h1>
              <p className="mt-4 text-[#c9bca4]">Verifying with Stripe.</p>
            </>
          ) : (
            <>
              <h1 className="font-serif-display text-[32px] sm:text-[44px] text-[#f4efe6]"
                  style={{ fontWeight: 600 }}>
                We couldn't confirm your payment yet.
              </h1>
              <p className="mt-4 text-[#c9bca4]">
                If you were charged, you'll receive a receipt from Stripe by email. If you have any questions, reply to that receipt or contact us.
              </p>
            </>
          )}
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/ai-operations" className="btn-ghost">Back to AI Operations</Link>
            <Link to="/" className="btn-gold">Home <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
